import { Icon } from '@coast/schemas';
import { isEmpty } from 'lodash';
import { Dispatch, Key, SetStateAction, useContext, useMemo } from 'react';
import { RCSubMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBTextOverlineBold } from '@shared/components/typography';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { NestedOptionsRow } from '../NestedOptionsRow';
import { MBWorkflowDefaultComponentOptions } from '../defaultOptions';
import { CombinedTagsMenu } from './CombinedTagsMenu';

interface Props {
  component: API.CombinedTagsComponentFragment;
  workflowTemplateId: string;
}

export function CombinedTagsOptions({ component, workflowTemplateId }: Props) {
  const extraSectionsGenerator = useOptions(workflowTemplateId, component);

  return (
    <MBWorkflowDefaultComponentOptions
      component={component}
      extraSections={extraSectionsGenerator}
      includePlaceholder={false}
    />
  );
}

function useOptions(workflowTemplateId: string, component: API.CombinedTagsComponentFragment) {
  const dispatch = useWorkflowDispatch();
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const colors = useContext(ColorThemeContext);
  const availableTagComponents = useMemo(
    () => workflowTemplate?.components.filter(isTagComponent) ?? [],
    [workflowTemplate?.components],
  );

  const onTagComponentSelected = (
    chosenComponent: API.TagComponent,
    setOpenKeys: Dispatch<SetStateAction<Key[]>>,
  ) => {
    setOpenKeys(prev => [...prev.filter(k => k !== 'combinedTagMenu'), 'combinedTagMenu']);
    const alreadySelected = component.componentIds?.includes(chosenComponent.id);
    const componentIds = [
      ...(component.componentIds?.filter(id => id !== chosenComponent.id) || []),
    ];

    if (!alreadySelected) {
      componentIds.push(chosenComponent.id);
    }

    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        componentIds,
      },
    });
  };

  return function Options({ setOpenKeys }) {
    return (
      <>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('builder.combinedTags.selectedTagField')}
          </MBTextOverlineBold>
        </View>
        <RCSubMenu
          key="combinedTagMenu"
          style={{ zIndex: 999 }}
          forceSubMenuRender
          title={
            <NestedOptionsRow
              name={
                isEmpty(component.componentIds)
                  ? i18n('builder.combinedTags.selectTagField')
                  : i18n('builder.combinedTags.selectedFields', {
                      count: component.componentIds.length,
                    })
              }
              defaultIcon={'tag' as Icon}
              selectedIcon={'tag' as Icon}
            />
          }
        >
          <CombinedTagsMenu
            selectedComponentIds={component.componentIds}
            components={availableTagComponents}
            onTagComponentSelect={chosenComponent => {
              onTagComponentSelected(chosenComponent as API.TagComponent, setOpenKeys);
            }}
          />
        </RCSubMenu>
      </>
    );
  };
}
